import * as types from '../constants/ActionTypes';
import * as constants from '../constants/AppConstants';

export const configureSentry = user => ({
  type: types.ADD_USER_CONTEXT_TO_SENTRY,
  payload: { user },
});

export const getAppStartData = () => ({
  type: types.GET_APP_START_DATA,
});

export const addMessage = (chatId, content, contentType, files = []) => ({
  type: types.ADD_MESSAGE,
  payload: {
    chatId,
    content,
    contentType,
    timeSent: new Date(),
    files,
  },
});

export const removeMessage = message => ({
  type: types.REMOVE_MESSAGE,
  payload: {
    message,
  },
});

export const addMessageFail = messageId => ({
  type: types.ADD_MESSAGE_FAIL,
  payload: {
    messageId,
  },
});

export const retryMessageFail = message => ({
  type: types.RETRY_MESSAGE_FAIL,
  payload: {
    message,
  },
});

export const deleteMessageFail = message => ({
  type: types.DELETE_MESSAGE_FAIL,
  payload: {
    message,
  },
});

export const selectMessage = (chatId, messageId, selectableType) => ({
  type: types.SELECT_MESSAGE,
  payload: { chatId, messageId, selectableType },
});

export const deselectMessages = chatId => ({
  type: types.DESELECT_MESSAGES,
  payload: { chatId },
});

export const getMessages = (chatId, page = 0, limit = constants.MESSAGE_LIMIT_SIZE) => ({
  type: types.GET_MESSAGES,
  payload: { chatId, page, limit },
});

export const searchUsers = (pattern = '') => ({
  type: types.SEARCH_USERS,
  payload: {
    pageNumber: 0,
    pageSize: 50,
    searchPattern: pattern,
    filterByPid: true,
  },
});

export const getAllChats = () => ({
  type: types.GET_ALL_CHATS,
  payload: {},
});

export const getChats = chatIds => ({
  type: types.GET_CHATS,
  payload: { chatIds },
});

export const getPublicChannels = () => ({
  type: types.GET_PUBLIC_CHANNELS,
  payload: {},
});

export const getIdentitySuccess = (user, practiceName, oAuthLogoutUrl, oAuthLoginUrl, memberId) => ({
  type: types.GET_IDENTITY_SUCCESS,
  payload: {
    ...user,
    practiceName,
    oAuthLogoutUrl,
    oAuthLoginUrl,
    memberId,
  },
});

export const startDirectMessage = userIds => ({
  type: types.START_DM,
  payload: { userIds },
});

export const startChannel = (name, isPrivate, userIds = []) => ({
  type: types.START_CHANNEL,
  payload: {
    name,
    userIds,
    private: isPrivate,
  },
});

export const addUsersToChannel = (chatId, userIds) => ({
  type: types.ADD_USERS_TO_CHANNEL,
  payload: { chatId, userIds },
});

export const markAsRead = (chatId, messageIds) => ({
  type: types.MARK_AS_READ,
  payload: { chatId, messageIds },
});

export const joinChannel = chatId => ({
  type: types.JOIN_CHANNEL,
  payload: { chatId },
});

export const leaveChannel = chatId => ({
  type: types.LEAVE_CHANNEL,
  payload: { chatId },
});

export const userLeftChannel = (userId, chatId) => ({
  type: types.USER_LEFT_CHANNEL,
  payload: { userId, chatId },
});

export const messageReceived = message => ({
  type: types.MESSAGE_RECEIVED,
  payload: message,
});

export const apiResponseReceived = message => ({
  type: types.API_RESPONSE_RECEIVED,
  payload: message,
});


export const setTyping = (chatId, typing) => ({
  type: types.SET_TYPING,
  payload: { chatId, typing },
});

export const typingReceived = (chatId, typistUserId) => ({
  type: types.TYPING_RECEIVED,
  payload: { chatId, typistUserId },
});

export const typingComplete = (chatId, typistUserId) => ({
  type: types.TYPING_COMPLETE,
  payload: { chatId, typistUserId },
});

export const revokeAttachmentUrl = attachmentId => ({
  type: types.REVOKE_ATTACHMENT_URL,
  payload: attachmentId,
});

/**
 * HTTP endpoint to download a file.  This is able to stream the file contents in binary and therefore,
 * it is much faster than its counterpart 'getAttachment'.  Use this call until websockets is configured to handle
 * binary streaming
 * @param attachmentId
 * @param isStreaming
 * @returns {{type: string, payload: {attachmentId: *}}}
 */
export const downloadAttachment = (attachmentId, isStreaming = false) => ({
  type: types.DOWNLOAD_ATTACHMENT,
  payload: { attachmentId, isStreaming },
});

export const downloadVideoImageAttachment = attachmentId => ({
  type: types.DOWNLOAD_VIDEO_IMAGE_ATTACHMENT,
  payload: { attachmentId },
});

/**
 * This attachment method is via websockets.  Since it becomes encoded with base64, it is slower than
 * its mirrored HTTP endpoint 'attachment.download'.
 * @param attachmentId
 * @returns {{type: string, payload: {attachmentId: *}}}
 *
 * Not used at this time
 */
export const getAttachment = (attachmentId) => {
  return {
    type: types.GET_ATTACHMENT,
    payload: { attachmentId },
  };
};

export const addError = (type, reason) => ({
  type: types.ADD_ERROR,
  payload: { type, reason },
});

export const clearError = type => ({
  type: types.CLEAR_ERROR,
  payload: { type },
});

export const mentionUser = (chatId, messageId) => ({
  type: types.USER_MENTIONED,
  payload: { chatId, messageId },
});

/**
 * Handles the notification from the backend that a new user was created.
 */
export const userCreated = user => ({
  type: types.USER_CREATED,
  payload: user,
});

export const userUpdated = user => ({
  type: types.USER_UPDATED,
  payload: user,
});

export const userStatusChanged = (userId, status) => ({
  type: types.USER_STATUS_CHANGED,
  payload: {
    userId,
    status,
  },
});

export const hideChat = chatIds => ({
  type: types.HIDE_CHAT,
  payload: {
    chatIds,
    hidden: true,
  },
});

/**
 * @param chatIds - array of chatIds
 */
export const showChat = chatIds => ({
  type: types.SHOW_CHAT,
  payload: {
    chatIds,
    hidden: false,
  },
});

export const getUnreadCount = chatId => ({
  type: types.GET_UNREAD_COUNT,
  payload: {
    chatId,
  },
});

export const incrementUnreadCount = chatId => ({
  type: types.INCREMENT_UNREAD_COUNT,
  payload: {
    chatId,
  },
});

export const clearUnreadCount = chatId => ({
  type: types.CLEAR_UNREAD_COUNT,
  payload: {
    chatId,
  },
});

export const saveScrollPosition = (chatId, scrollPosition) => ({
  type: types.SAVE_SCROLL_POSITION,
  payload: {
    chatId,
    scrollPosition,
  },
});

export const saveMessageInputData = (chatId, messageInputData) => ({
  type: types.SAVE_MESSAGE_INPUT_DATA,
  payload: {
    chatId,
    messageInputData,
  },
});
